import { useState, useEffect } from "react";
import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, FolderOpen, GraduationCap, MessageSquare,
  Users, Handshake, LogOut, Menu, X, Briefcase, Settings, ExternalLink, Shield, UserCog, Sun, Moon, Star
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useSettings } from "@/contexts/SettingsContext";
import { useTheme } from "@/contexts/ThemeContext";

const sidebarLinks = [
  { href: "/admin/dashboard", label: "لوحة التحكم", icon: LayoutDashboard },
  { href: "/admin/dashboard?tab=portfolio", label: "الأعمال", icon: FolderOpen },
  { href: "/admin/dashboard?tab=courses", label: "الدورات", icon: GraduationCap },
  { href: "/admin/dashboard?tab=jobs", label: "التوظيف", icon: Briefcase },
  { href: "/admin/dashboard?tab=testimonials", label: "آراء العملاء", icon: MessageSquare },
  { href: "/admin/dashboard?tab=partners", label: "الشركاء", icon: Handshake },
  { href: "/admin/dashboard?tab=influencers", label: "صناع المحتوى", icon: Star },
  { href: "/admin/dashboard?tab=messages", label: "الرسائل", icon: Users },
  { href: "/admin/dashboard?tab=employees", label: "الموظفين", icon: UserCog },
  { href: "/admin/dashboard?tab=users", label: "المستخدمين", icon: Shield },
];

export default function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const settings = useSettings();
  const { theme, toggleTheme } = useTheme();

  useEffect(() => {
    const token = localStorage.getItem("admin_token");
    if (!token) navigate("/admin/login");
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem("admin_token");
    navigate("/admin/login");
  };

  const isActive = (href) => {
    const url = new URL(href, window.location.origin);
    const tab = url.searchParams.get("tab");
    const currentTab = new URLSearchParams(location.search).get("tab");
    if (!tab && !currentTab && location.pathname === "/admin/dashboard") return true;
    return tab === currentTab;
  };

  return (
    <div dir="rtl" className="min-h-screen bg-background flex">
      {/* Sidebar */}
      <aside
        className={`fixed lg:static inset-y-0 right-0 z-50 w-64 bg-[#0a0a0b] border-l border-white/5 transform transition-transform lg:transform-none ${
          sidebarOpen ? "translate-x-0" : "translate-x-full lg:translate-x-0"
        }`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="block">
              {(settings.logo_dark_url || settings.logo_url) ? (
                <img src={settings.logo_dark_url || settings.logo_url} alt={settings.site_name} className="h-10 object-contain" />
              ) : (
                <span className="text-xl font-bold font-['Zain'] text-white">{settings.site_name || "رؤية"}</span>
              )}
            </Link>
            <p className="text-xs text-zinc-500 mt-1">لوحة التحكم</p>
          </div>

          <nav className="flex-1 p-4 space-y-1">
            {sidebarLinks.map((link) => {
              const Icon = link.icon;
              return (
                <Link
                  key={link.href}
                  to={link.href}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                    isActive(link.href)
                      ? "bg-primary/10 text-primary"
                      : "text-zinc-400 hover:text-white hover:bg-white/5"
                  }`}
                  data-testid={`admin-nav-${link.label}`}
                >
                  <Icon size={18} />
                  {link.label}
                </Link>
              );
            })}
          </nav>

          <div className="p-4 border-t border-white/5 space-y-1">
            <Link
              to="/admin/dashboard?tab=settings"
              onClick={() => setSidebarOpen(false)}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-lg text-sm font-medium transition-colors ${
                isActive("/admin/dashboard?tab=settings")
                  ? "bg-primary/10 text-primary"
                  : "text-zinc-400 hover:text-white hover:bg-white/5"
              }`}
              data-testid="admin-nav-الإعدادات"
            >
              <Settings size={18} />
              الإعدادات
            </Link>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-zinc-400 hover:text-yellow-400"
              onClick={toggleTheme}
              data-testid="admin-theme-toggle"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
              {theme === "dark" ? "الوضع النهاري" : "الوضع الليلي"}
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-zinc-400 hover:text-red-400"
              onClick={handleLogout}
              data-testid="admin-logout-btn"
            >
              <LogOut size={18} />
              تسجيل الخروج
            </Button>
          </div>
        </div>
      </aside>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main */}
      <div className="flex-1 min-h-screen">
        <header className="h-16 bg-[#0a0a0b]/80 backdrop-blur border-b border-white/5 flex items-center justify-between px-4 lg:px-6">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden text-white"
              onClick={() => setSidebarOpen(true)}
              data-testid="admin-menu-toggle"
            >
              <Menu size={20} />
            </Button>
            <Link
              to="/"
              target="_blank"
              className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-primary/10 text-primary text-sm font-medium hover:bg-primary/20 transition-colors"
              data-testid="admin-home-link"
            >
              <ExternalLink size={14} />
              الصفحة الرئيسية
            </Link>
          </div>
          <span className="text-sm text-zinc-400">مرحبًا، المشرف</span>
        </header>
        <div className="p-4 lg:p-6">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
